// PDF Parser Service
// Extracts experience from PDF files (including gofullpage exports)

// Lazy load pdfjs to avoid issues on initial page load
let pdfjsLib = null;
let workerInitialized = false;
let tesseractLib = null;

const initializePDFJS = async () => {
  if (pdfjsLib && workerInitialized) {
    return pdfjsLib;
  }

  try {
    // Dynamic import to avoid loading PDF.js until needed
    pdfjsLib = await import('pdfjs-dist');
    
    if (!pdfjsLib) {
      throw new Error('PDF.js library import returned undefined');
    }
    
    // Set worker path - use a more reliable approach
    if (typeof window !== 'undefined' && !workerInitialized) {
      try {
        // Get the actual library (handle both default export and named export)
        const actualLib = pdfjsLib.default || pdfjsLib;
        
        // Get the version from the imported library - use installed version (5.4.530)
        const version = actualLib.version || '5.4.530';
        
        // For pdfjs-dist 5.x, use CDN worker (more reliable with Vite)
        // Try CDN first as it's more reliable, then fallback to local
        let workerPath = 'https://unpkg.com/pdfjs-dist@5.4.530/build/pdf.worker.min.mjs';
        
        // Set worker source - for v5.x, the worker should be loaded as a module
        if (actualLib.GlobalWorkerOptions) {
          actualLib.GlobalWorkerOptions.workerSrc = workerPath;
          
          // Also set useSystemFonts to false to avoid font loading issues
          if (actualLib.GlobalWorkerOptions.useSystemFonts === undefined) {
            actualLib.GlobalWorkerOptions.useSystemFonts = false;
          }
        }
        
        // Verify the library is properly initialized
        if (!actualLib.getDocument) {
          throw new Error('PDF.js getDocument method not found');
        }
        
        workerInitialized = true;
        console.log('PDF.js initialized successfully with version:', version, 'worker:', workerPath);
      } catch (workerError) {
        console.error('Error setting up PDF.js worker:', workerError);
        // Try alternative worker paths as fallback
        const actualLib = pdfjsLib.default || pdfjsLib;
        const fallbackPaths = [
          // Try CDN first (more reliable with Vite)
          'https://unpkg.com/pdfjs-dist@5.4.530/build/pdf.worker.min.mjs',
          'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.530/build/pdf.worker.min.mjs',
          // Then try local worker from public folder
          '/pdf.worker.min.mjs'
        ];
        
        let workerSet = false;
        for (const fallbackPath of fallbackPaths) {
          try {
            if (actualLib.GlobalWorkerOptions) {
              actualLib.GlobalWorkerOptions.workerSrc = fallbackPath;
              workerSet = true;
              console.log('PDF.js worker set to:', fallbackPath);
              break;
            }
          } catch (e) {
            console.warn('Worker path failed:', fallbackPath, e);
          }
        }
        
        if (!workerSet) {
          throw new Error('Failed to set PDF.js worker path. Please check your internet connection and try again.');
        }
        
        workerInitialized = true;
      }
    }
    
    // Return the library (handle both default export and named export)
    // Store the actual library for later use
    const actualLib = pdfjsLib.default || pdfjsLib;
    pdfjsLib = actualLib; // Update the stored reference
    return actualLib;
  } catch (error) {
    console.error('Failed to initialize PDF.js:', error);
    const errorMessage = error.message || 'Unknown error';
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('network')) {
      throw new Error('Failed to load PDF processing library due to network error. Please check your internet connection and try again.');
    } else if (errorMessage.includes('import')) {
      throw new Error('Failed to load PDF processing library. The library may not be installed. Please contact support.');
    } else {
      throw new Error(`Failed to load PDF processing library: ${errorMessage}. Please refresh the page and try again.`);
    }
  }
};

/**
 * Initialize Tesseract.js for OCR
 */
const initializeTesseract = async () => {
  if (tesseractLib) {
    return tesseractLib;
  }
  
  try {
    // Dynamic import to avoid loading Tesseract until needed
    tesseractLib = await import('tesseract.js');
    return tesseractLib.default || tesseractLib;
  } catch (error) {
    console.warn('Tesseract.js not available. OCR functionality will be disabled.', error);
    return null;
  }
};

/**
 * Extract text from PDF using OCR (for scanned PDFs)
 */
const extractTextWithOCR = async (pdf) => {
  try {
    const tesseract = await initializeTesseract();
    if (!tesseract) {
      throw new Error('OCR library not available');
    }
    
    const { createWorker } = tesseract;
    const worker = await createWorker('eng');
    
    let fullText = '';
    const numPages = pdf.numPages || 0;
    
    // Process first 5 pages to avoid timeout (most resumes are 1-2 pages)
    const maxPages = Math.min(numPages, 5);
    
    for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
      try {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 2.0 }); // Higher scale for better OCR
        
        // Create canvas to render PDF page
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        // Render PDF page to canvas
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };
        
        await page.render(renderContext).promise;
        
        // Use OCR to extract text from canvas
        const { data: { text } } = await worker.recognize(canvas);
        if (text && text.trim().length > 0) {
          fullText += text.trim() + '\n';
        }
      } catch (pageError) {
        console.warn(`Error processing page ${pageNum} with OCR:`, pageError);
        // Continue with other pages
      }
    }
    
    await worker.terminate();
    
    return fullText.trim();
  } catch (error) {
    console.error('OCR extraction error:', error);
    throw error;
  }
};

/**
 * Extract text from PDF file
 */
export const extractTextFromPDF = async (file) => {
  try {
    // Initialize PDF.js if not already done
    let pdfjs;
    try {
      pdfjs = await initializePDFJS();
    } catch (initError) {
      console.error('PDF.js initialization error:', initError);
      throw new Error(`PDF processing library failed to initialize: ${initError.message}. Please refresh the page and try again.`);
    }
    
    if (!pdfjs) {
      throw new Error('PDF processing library failed to load. Please refresh the page and try again.');
    }
    
    // Ensure getDocument is available
    const getDocument = pdfjs.getDocument || pdfjs.default?.getDocument;
    if (!getDocument) {
      throw new Error('PDF.js getDocument method is not available. The library may not have loaded correctly.');
    }

    // Validate file first
    if (!file || file.size === 0) {
      throw new Error('The PDF file is empty. Please select a valid PDF file.');
    }
    
    // Check if file is actually a PDF by checking magic bytes
    const fileStart = await file.slice(0, 4).arrayBuffer();
    const uint8Array = new Uint8Array(fileStart);
    const pdfHeader = String.fromCharCode(...uint8Array.slice(0, 4));
    
    if (pdfHeader !== '%PDF') {
      throw new Error('The file does not appear to be a valid PDF. Please ensure you are uploading a PDF file (not an image or other file type).');
    }
    
    const arrayBuffer = await file.arrayBuffer();
    
    // Load PDF with error handling and retry logic
    let pdf;
    let lastError = null;
    
    // Try different loading options - for pdfjs-dist 5.x
    const loadOptions = [
      { 
        data: arrayBuffer,
        verbosity: 0,
        useSystemFonts: false,
        disableFontFace: false
      },
      { 
        data: arrayBuffer,
        verbosity: 0,
        useSystemFonts: false
      },
      { 
        data: arrayBuffer,
        verbosity: 0
      }
    ];
    
    for (let i = 0; i < loadOptions.length; i++) {
      try {
        pdf = await getDocument(loadOptions[i]).promise;
        break; // Success, exit loop
      } catch (loadError) {
        console.error(`PDF loading attempt ${i + 1} failed:`, loadError);
        lastError = loadError;
        
        // If this is the last attempt, throw the error
        if (i === loadOptions.length - 1) {
          if (loadError.name === 'InvalidPDFException' || loadError.message?.includes('Invalid PDF')) {
            throw new Error('Invalid PDF file. The file may be corrupted, incomplete, or in an unsupported format. Please try: 1) Re-saving the PDF, 2) Using a different PDF file, or 3) Converting the file to PDF format if it\'s currently in another format.');
          } else if (loadError.name === 'PasswordException' || loadError.message?.includes('password')) {
            throw new Error('This PDF is password-protected. Please remove the password protection and try again.');
          } else if (loadError.message?.includes('Missing PDF header') || loadError.message?.includes('Invalid PDF structure')) {
            throw new Error('The PDF file structure is invalid. The file may be corrupted. Please try re-saving or re-exporting the PDF.');
          } else {
            throw new Error(`Failed to load PDF: ${loadError.message || 'The file may be corrupted, encrypted, or in an unsupported format. Please try a different PDF file or use the TXT import option instead.'}`);
          }
        }
        // Continue to next attempt
      }
    }
    
    let fullText = '';
    
    // Extract text from all pages
    try {
      const numPages = pdf.numPages || 0;
      if (numPages === 0) {
        throw new Error('PDF appears to be empty or invalid.');
      }

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        try {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          
          // Preserve line structure for better parsing
          // Group text items by their Y position to detect line breaks
          const items = textContent.items || [];
          if (items.length === 0) {
            continue;
          }
          
          // Sort items by Y position (top to bottom)
          const sortedItems = [...items].sort((a, b) => {
            const yA = a.transform ? a.transform[5] : 0;
            const yB = b.transform ? b.transform[5] : 0;
            return yB - yA; // Higher Y = top of page
          });
          
          let pageText = '';
          let lastY = null;
          const lineThreshold = 5; // Pixels - if Y difference is more than this, it's a new line
          
          for (const item of sortedItems) {
            const str = (item.str || '').trim();
            if (str.length === 0) continue;
            
            const currentY = item.transform ? item.transform[5] : 0;
            
            // Check if this is a new line
            if (lastY !== null && Math.abs(currentY - lastY) > lineThreshold) {
              pageText += '\n';
            } else if (lastY !== null && Math.abs(currentY - lastY) <= lineThreshold) {
              // Same line, add space if needed
              if (pageText.length > 0 && !pageText.endsWith(' ') && !pageText.endsWith('\n')) {
                pageText += ' ';
              }
            }
            
            pageText += str;
            lastY = currentY;
          }
          
          if (pageText) {
            fullText += pageText + '\n\n'; // Double newline between pages
          }
        } catch (pageError) {
          console.warn(`Error extracting text from page ${pageNum}:`, pageError);
          // Continue with other pages
        }
      }
    } catch (pageError) {
      console.error('Error extracting text from pages:', pageError);
      // If we got some text, return it; otherwise throw
      if (fullText.trim().length === 0) {
        throw new Error('Failed to extract text from PDF pages. The PDF may contain only images or be encrypted.');
      }
    }
    
    if (fullText.trim().length === 0) {
      // Try OCR as fallback for scanned PDFs
      console.log('No text found in PDF, attempting OCR...');
      try {
        const ocrText = await extractTextWithOCR(pdf);
        if (ocrText && ocrText.trim().length > 0) {
          console.log('OCR extraction successful');
          return ocrText;
        }
      } catch (ocrError) {
        console.warn('OCR extraction failed:', ocrError);
        // Fall through to throw the original error
      }
      throw new Error('No text found in PDF. The PDF may contain only images. Please use a PDF with selectable text.');
    }
    
    return fullText;
  } catch (error) {
    console.error('Error extracting text from PDF:', error);
    // Re-throw with user-friendly message if it's our custom error
    if (error.message && (
      error.message.includes('Failed to') || 
      error.message.includes('No text') ||
      error.message.includes('Invalid PDF') ||
      error.message.includes('password-protected') ||
      error.message.includes('empty or invalid')
    )) {
      throw error;
    }
    throw new Error('Failed to extract text from PDF. Please ensure the file is a valid PDF with readable text.');
  }
};

/**
 * Parse experience lines extracted from EXPERIENCE section
 * Very flexible parsing - extracts any information that looks like job entries
 */
const parseExperienceLines = (lines) => {
  const experiences = [];
  let currentEntry = null;
  let descriptionLines = [];
  
  // Job-related keywords
  const jobKeywords = [
    'engineer', 'manager', 'director', 'analyst', 'developer', 'specialist', 
    'coordinator', 'assistant', 'executive', 'officer', 'president', 'vice president',
    'consultant', 'advisor', 'lead', 'senior', 'junior', 'associate', 'chief',
    'designer', 'architect', 'scientist', 'researcher', 'administrator', 'technician'
  ];
  
  // Company indicators
  const companyIndicators = ['inc', 'llc', 'corp', 'company', 'ltd', 'systems', 'solutions', 'technologies', 'group', 'associates', 'consulting'];
  
  // Date patterns
  const dateRangePattern = /(\d{4}|\w+\s+\d{4}|\d{1,2}\/\d{4})\s*[-–—]\s*(\d{4}|\w+\s+\d{4}|\d{1,2}\/\d{4}|Present|Current)/i;
  const singleDatePattern = /(\d{4}|\w+\s+\d{4}|\d{1,2}\/\d{4})/;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lowerLine = line.toLowerCase();
    
    // Skip empty lines and section headers
    if (line.length < 3) continue;
    if (/^(EXPERIENCE|EDUCATION|SKILLS|CERTIFICATIONS|PROJECTS)$/i.test(line)) continue;
    
    // Check for date ranges (likely start of a new entry)
    const dateRangeMatch = line.match(dateRangePattern);
    const hasJobKeyword = jobKeywords.some(keyword => lowerLine.includes(keyword));
    const hasCompanyIndicator = companyIndicators.some(indicator => lowerLine.includes(indicator));
    const hasDate = singleDatePattern.test(line);
    
    // Pattern 1: Line with date range - likely a new entry
    if (dateRangeMatch) {
      // Save previous entry
      if (currentEntry && (currentEntry.title || currentEntry.company)) {
        if (descriptionLines.length > 0) {
          currentEntry.description = descriptionLines.join(' ').substring(0, 1000);
        }
        experiences.push(currentEntry);
      }
      
      // Start new entry with dates
      currentEntry = {
        title: '',
        company: '',
        location: '',
        start_date: dateRangeMatch[1] || '',
        end_date: dateRangeMatch[2] || 'Present',
        description: ''
      };
      descriptionLines = [];
      
      // Look for title/company in surrounding lines
      // Check previous line
      if (i > 0) {
        const prevLine = lines[i - 1];
        if (prevLine.length > 5 && prevLine.length < 100 && !singleDatePattern.test(prevLine)) {
          if (hasJobKeyword && prevLine.toLowerCase().includes(jobKeywords.find(k => prevLine.toLowerCase().includes(k)))) {
            currentEntry.title = prevLine;
          } else if (hasCompanyIndicator || prevLine.length < 50) {
            currentEntry.company = prevLine;
          } else {
            currentEntry.title = prevLine;
          }
        }
      }
      
      // Check next line
      if (i < lines.length - 1) {
        const nextLine = lines[i + 1];
        if (nextLine.length > 5 && nextLine.length < 100 && !singleDatePattern.test(nextLine)) {
          if (!currentEntry.title) {
            currentEntry.title = nextLine;
          } else if (!currentEntry.company) {
            currentEntry.company = nextLine;
          }
        }
      }
    }
    // Pattern 2: Line with "Title at Company" or "Company - Title"
    else if (hasJobKeyword || hasCompanyIndicator) {
      const atPattern = /(.+?)\s+(?:at|@)\s+(.+)/i;
      const dashPattern = /(.+?)\s*[-–—]\s*(.+)/;
      
      if (atPattern.test(line)) {
        const match = line.match(atPattern);
        // Save previous entry
        if (currentEntry && (currentEntry.title || currentEntry.company)) {
          if (descriptionLines.length > 0) {
            currentEntry.description = descriptionLines.join(' ').substring(0, 1000);
          }
          experiences.push(currentEntry);
        }
        
        currentEntry = {
          title: match[1].trim(),
          company: match[2].trim(),
          location: '',
          start_date: '',
          end_date: 'Present',
          description: ''
        };
        descriptionLines = [];
      } else if (dashPattern.test(line)) {
        const match = line.match(dashPattern);
        // Save previous entry
        if (currentEntry && (currentEntry.title || currentEntry.company)) {
          if (descriptionLines.length > 0) {
            currentEntry.description = descriptionLines.join(' ').substring(0, 1000);
          }
          experiences.push(currentEntry);
        }
        
        // Determine which is title and which is company
        const part1 = match[1].trim();
        const part2 = match[2].trim();
        const part1Lower = part1.toLowerCase();
        
        if (jobKeywords.some(k => part1Lower.includes(k))) {
          currentEntry = {
            title: part1,
            company: part2,
            location: '',
            start_date: '',
            end_date: 'Present',
            description: ''
          };
        } else {
          currentEntry = {
            title: part2,
            company: part1,
            location: '',
            start_date: '',
            end_date: 'Present',
            description: ''
          };
        }
        descriptionLines = [];
      } else if (line.length > 5 && line.length < 100) {
        // Single line that might be title or company
        if (!currentEntry) {
          currentEntry = {
            title: hasJobKeyword ? line : '',
            company: hasCompanyIndicator ? line : '',
            location: '',
            start_date: '',
            end_date: 'Present',
            description: ''
          };
          descriptionLines = [];
        } else {
          // Add to current entry
          if (!currentEntry.title && hasJobKeyword) {
            currentEntry.title = line;
          } else if (!currentEntry.company && (hasCompanyIndicator || line.length < 50)) {
            currentEntry.company = line;
          }
        }
      }
    }
    // Pattern 3: Description text (longer lines, no dates, no keywords)
    else if (currentEntry && line.length > 20 && !hasDate && !hasJobKeyword && !hasCompanyIndicator) {
      descriptionLines.push(line);
    }
    // Pattern 4: Single date (might be start or end date)
    else if (currentEntry && hasDate && !dateRangeMatch) {
      const dateMatch = line.match(singleDatePattern);
      if (dateMatch && !currentEntry.start_date) {
        currentEntry.start_date = dateMatch[1];
      }
    }
    // Pattern 5: Location or other metadata
    else if (currentEntry && (line.toLowerCase().includes('location') || line.toLowerCase().includes('city'))) {
      const locationMatch = line.match(/(?:location|city)[\s:]*([^,]+(?:,\s*[^,]+)?)/i);
      if (locationMatch) {
        currentEntry.location = locationMatch[1].trim();
      }
    }
  }
  
  // Save last entry
  if (currentEntry && (currentEntry.title || currentEntry.company)) {
    if (descriptionLines.length > 0) {
      currentEntry.description = descriptionLines.join(' ').substring(0, 1000);
    }
    experiences.push(currentEntry);
  }
  
  return experiences;
};

/**
 * Parse experience from extracted PDF text
 * Handles various resume formats including gofullpage exports
 * Prioritizes "EXPERIENCE" section (as LinkedIn uses) over "WORK EXPERIENCE"
 */
export const parseWorkExperienceFromText = (text) => {
  const experiences = [];
  
  // Log raw text for debugging (first 2000 chars)
  console.log('Raw PDF text (first 2000 chars):', text.substring(0, 2000));
  
  // STEP 1: Find the EXPERIENCE section header and extract everything under it
  const allLines = text.split(/\r?\n/).map(line => line.trim()).filter(line => line.length > 0);
  
  // Find the line that says "EXPERIENCE" (case-insensitive, may have extra spaces)
  let experienceStartIndex = -1;
  const experienceHeaderPatterns = [
    /^\s*EXPERIENCE\s*$/i,
    /^\s*EXPERIENCE\s*[:•\-]?\s*$/i,
    /^\s*WORK\s+EXPERIENCE\s*$/i,
    /^\s*PROFESSIONAL\s+EXPERIENCE\s*$/i,
    /^\s*EMPLOYMENT\s+HISTORY\s*$/i
  ];
  
  for (let i = 0; i < allLines.length; i++) {
    const line = allLines[i];
    if (experienceHeaderPatterns.some(pattern => pattern.test(line))) {
      experienceStartIndex = i + 1; // Start from the line after "EXPERIENCE"
      console.log(`Found EXPERIENCE section at line ${i + 1}: "${line}"`);
      break;
    }
  }
  
  // If we found the EXPERIENCE section, extract everything until the next major section
  let experienceLines = [];
  if (experienceStartIndex >= 0) {
    // Section end markers
    const sectionEndPatterns = [
      /^\s*EDUCATION\s*$/i,
      /^\s*SKILLS\s*$/i,
      /^\s*CERTIFICATIONS\s*$/i,
      /^\s*PROJECTS\s*$/i,
      /^\s*AWARDS\s*$/i,
      /^\s*PUBLICATIONS\s*$/i,
      /^\s*LANGUAGES\s*$/i,
      /^\s*INTERESTS\s*$/i,
      /^\s*SUMMARY\s*$/i,
      /^\s*ABOUT\s*$/i
    ];
    
    // Extract all lines from EXPERIENCE until next section
    for (let i = experienceStartIndex; i < allLines.length; i++) {
      const line = allLines[i];
      // Stop if we hit another major section
      if (sectionEndPatterns.some(pattern => pattern.test(line))) {
        console.log(`Stopping at section: "${line}"`);
        break;
      }
      experienceLines.push(line);
    }
    
    console.log(`Extracted ${experienceLines.length} lines from EXPERIENCE section`);
  } else {
    // If no EXPERIENCE header found, use all lines
    console.log('No EXPERIENCE section header found, using all text');
    experienceLines = allLines;
  }
  
  // If we have experience lines, parse them
  if (experienceLines.length > 0) {
    console.log('Parsing experience lines:', experienceLines.slice(0, 20).join(' | '));
    experiences.push(...parseExperienceLines(experienceLines));
  }
  
  // Keep original text with line breaks for line-by-line parsing
  // Also create normalized version for pattern matching
  const normalizedText = text.replace(/\s+/g, ' ').trim();
  
  // If we didn't find any experiences from the section-based parsing, try pattern matching
  if (experiences.length === 0) {
    // Common patterns for experience sections
    // Prioritize "EXPERIENCE" (LinkedIn format) over "WORK EXPERIENCE"
    const experiencePatterns = [
      // Pattern 1: "EXPERIENCE" section (LinkedIn format - prioritize this)
      /(?:^|\n)\s*EXPERIENCE\s*[\s\S]*?(?=(?:EDUCATION|SKILLS|CERTIFICATIONS|PROJECTS|WORK\s+EXPERIENCE|PROFESSIONAL\s+EXPERIENCE|EMPLOYMENT\s+HISTORY|$))/i,
      // Pattern 2: "WORK EXPERIENCE" or other variations (fallback)
      /(?:WORK\s+EXPERIENCE|PROFESSIONAL\s+EXPERIENCE|EMPLOYMENT\s+HISTORY)[\s\S]*?(?=(?:EDUCATION|SKILLS|CERTIFICATIONS|PROJECTS|$))/i,
    ];
    
    // Try to find experience section first
    let experienceSection = '';
    for (const pattern of experiencePatterns) {
      const match = normalizedText.match(pattern);
      if (match && match[0]) {
        experienceSection = match[0];
        break;
      }
    }
    
    // If no section found, use the whole text
    const searchText = experienceSection || normalizedText;
    
    // Extract individual job entries from the section
    // Pattern: Job Title at Company | Date - Date | Location
    const jobPatterns = [
      // Pattern 1: Title at Company, Date - Date, Location
      /([A-Z][A-Za-z\s&,.-]+(?:Manager|Engineer|Developer|Analyst|Specialist|Director|Lead|Senior|Junior|Associate|Coordinator|Consultant|Advisor|Executive|Officer|President|Vice President|VP|Chief|CTO|CEO|CFO|COO)?)\s+(?:at|@)\s+([A-Z][A-Za-z\s&,.-]+(?:Inc|LLC|Corp|Company|Ltd|Systems|Solutions|Technologies|Group|Associates)?)[\s,]+([A-Z][a-z]+\s+\d{4})\s*[-–—]\s*([A-Z][a-z]+\s+\d{4}|Present|Current)[\s,]+([A-Z][A-Za-z\s,]+)?/gi,
      // Pattern 2: Company | Title | Date - Date
      /([A-Z][A-Za-z\s&,.-]+(?:Inc|LLC|Corp|Company|Ltd|Systems|Solutions|Technologies|Group|Associates)?)\s*[|•]\s*([A-Z][A-Za-z\s&,.-]+(?:Manager|Engineer|Developer|Analyst|Specialist|Director|Lead|Senior|Junior|Associate|Coordinator|Consultant|Advisor|Executive|Officer|President|Vice President|VP|Chief|CTO|CEO|CFO|COO)?)\s*[|•]\s*([A-Z][a-z]+\s+\d{4})\s*[-–—]\s*([A-Z][a-z]+\s+\d{4}|Present|Current)/gi,
      // Pattern 3: Date - Date | Title | Company
      /([A-Z][a-z]+\s+\d{4})\s*[-–—]\s*([A-Z][a-z]+\s+\d{4}|Present|Current)\s*[|•]\s*([A-Z][A-Za-z\s&,.-]+(?:Manager|Engineer|Developer|Analyst|Specialist|Director|Lead|Senior|Junior|Associate|Coordinator|Consultant|Advisor|Executive|Officer|President|Vice President|VP|Chief|CTO|CEO|CFO|COO)?)\s*[|•]\s*([A-Z][A-Za-z\s&,.-]+(?:Inc|LLC|Corp|Company|Ltd|Systems|Solutions|Technologies|Group|Associates)?)/gi,
    ];
  
  const foundJobs = new Set(); // To avoid duplicates
  
  for (const pattern of jobPatterns) {
    let match;
    while ((match = pattern.exec(searchText)) !== null) {
      let title, company, startDate, endDate, location;
      
      // Extract based on pattern structure
      if (match.length >= 4) {
        if (pattern === jobPatterns[0]) {
          // Title at Company, Date - Date, Location
          title = match[1]?.trim();
          company = match[2]?.trim();
          startDate = match[3]?.trim();
          endDate = match[4]?.trim();
          location = match[5]?.trim() || '';
        } else if (pattern === jobPatterns[1]) {
          // Company | Title | Date - Date
          company = match[1]?.trim();
          title = match[2]?.trim();
          startDate = match[3]?.trim();
          endDate = match[4]?.trim();
          location = '';
        } else if (pattern === jobPatterns[2]) {
          // Date - Date | Title | Company
          startDate = match[1]?.trim();
          endDate = match[2]?.trim();
          title = match[3]?.trim();
          company = match[4]?.trim();
          location = '';
        }
        
        // Create unique key to avoid duplicates
        const jobKey = `${title}_${company}_${startDate}`.toLowerCase();
        
        if (title && company && !foundJobs.has(jobKey)) {
          foundJobs.add(jobKey);
          
          // Extract description (text after the job entry, before next job)
          let description = '';
          const matchIndex = match.index;
          const nextMatch = pattern.exec(searchText);
          if (nextMatch) {
            description = searchText.substring(matchIndex + match[0].length, nextMatch.index)
              .trim()
              .substring(0, 500); // Limit description length
            pattern.lastIndex = nextMatch.index; // Reset to continue from next match
          }
          
          experiences.push({
            title: title,
            company: company,
            location: location || '',
            start_date: startDate || '',
            end_date: endDate || 'Present',
            description: description
          });
        }
      }
    }
    }
  }
  
  // Alternative: Enhanced line-by-line parsing for LinkedIn and gofullpage formats
  if (experiences.length === 0) {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    let currentJob = null;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Look for date patterns (e.g., "Jan 2020 - Dec 2022" or "2020 - 2022" or "Jan 2020 - Present")
      const datePattern = /([A-Z][a-z]+\s+\d{4}|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec\s+\d{4}|\d{1,2}\/\d{4}|\d{4})\s*[-–—]\s*([A-Z][a-z]+\s+\d{4}|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec\s+\d{4}|\d{1,2}\/\d{4}|\d{4}|Present|Current)/i;
      const dateMatch = line.match(datePattern);
      
      // Also check for LinkedIn format: "Title at Company" or "Company | Title"
      const linkedInPattern1 = /^([A-Z][A-Za-z\s&,.-]+(?:Manager|Engineer|Developer|Analyst|Specialist|Director|Lead|Senior|Junior|Associate|Coordinator|Consultant|Advisor|Executive|Officer|President|Vice President|VP|Chief|CTO|CEO|CFO|COO)?)\s+(?:at|@)\s+([A-Z][A-Za-z\s&,.-]+(?:Inc|LLC|Corp|Company|Ltd|Systems|Solutions|Technologies|Group|Associates)?)/i;
      const linkedInPattern2 = /^([A-Z][A-Za-z\s&,.-]+(?:Inc|LLC|Corp|Company|Ltd|Systems|Solutions|Technologies|Group|Associates)?)\s*[|•]\s*([A-Z][A-Za-z\s&,.-]+(?:Manager|Engineer|Developer|Analyst|Specialist|Director|Lead|Senior|Junior|Associate|Coordinator|Consultant|Advisor|Executive|Officer|President|Vice President|VP|Chief|CTO|CEO|CFO|COO)?)/i;
      
      const linkedInMatch1 = line.match(linkedInPattern1);
      const linkedInMatch2 = line.match(linkedInPattern2);
      
      if (dateMatch || linkedInMatch1 || linkedInMatch2) {
        // Save previous job if exists
        if (currentJob && (currentJob.title || currentJob.company)) {
          experiences.push(currentJob);
        }
        
        // Start new job
        currentJob = {
          start_date: dateMatch ? (dateMatch[1] || '') : '',
          end_date: dateMatch ? (dateMatch[2] || 'Present') : 'Present',
          title: linkedInMatch1 ? linkedInMatch1[1].trim() : (linkedInMatch2 ? linkedInMatch2[2].trim() : ''),
          company: linkedInMatch1 ? linkedInMatch1[2].trim() : (linkedInMatch2 ? linkedInMatch2[1].trim() : ''),
          location: '',
          description: ''
        };
        
        // Look for title and company in surrounding lines if not found
        if (!currentJob.title || !currentJob.company) {
          // Check previous 2 lines
          for (let j = Math.max(0, i - 2); j < i; j++) {
            const prevLine = lines[j];
            if (!prevLine.match(datePattern) && prevLine.length > 2 && prevLine.length < 100) {
              if (!currentJob.title && !prevLine.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|\d{4})/i)) {
                currentJob.title = prevLine;
              } else if (!currentJob.company && currentJob.title !== prevLine && !prevLine.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|\d{4})/i)) {
                currentJob.company = prevLine;
              }
            }
          }
          
          // Check next 2 lines
          for (let j = i + 1; j < Math.min(lines.length, i + 3); j++) {
            const nextLine = lines[j];
            if (!nextLine.match(datePattern) && nextLine.length > 2 && nextLine.length < 100) {
              if (!currentJob.title && !nextLine.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|\d{4})/i)) {
                currentJob.title = nextLine;
              } else if (!currentJob.company && currentJob.title !== nextLine && !nextLine.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|\d{4})/i)) {
                currentJob.company = nextLine;
              }
            }
          }
        }
      } else if (currentJob) {
        // Accumulate description
        if (line.length > 10 && !line.match(/^[A-Z\s&,.-]+$/) && !line.match(datePattern) && !line.match(linkedInPattern1) && !line.match(linkedInPattern2)) {
          currentJob.description += (currentJob.description ? ' ' : '') + line;
        }
      }
    }
    
    // Add last job
    if (currentJob && (currentJob.title || currentJob.company)) {
      experiences.push(currentJob);
    }
  }
  
  // Additional fallback: Try to extract from any text that looks like job entries
  if (experiences.length === 0) {
    // Look for patterns like "Company Name" followed by "Job Title" followed by dates
    const allLines = text.split(/\r?\n/).map(line => line.trim()).filter(line => line.length > 0);
    
    for (let i = 0; i < allLines.length - 2; i++) {
      const line1 = allLines[i];
      const line2 = allLines[i + 1];
      const line3 = allLines[i + 2];
      
      // Pattern: Company name (capitalized, reasonable length), then title, then dates
      const companyPattern = /^[A-Z][A-Za-z0-9\s&,.-]{3,50}(?:Inc|LLC|Corp|Company|Ltd|Systems|Solutions|Technologies|Group|Associates)?$/;
      const titlePattern = /^[A-Z][A-Za-z\s&,.-]{5,60}(?:Manager|Engineer|Developer|Analyst|Specialist|Director|Lead|Senior|Junior|Associate|Coordinator|Consultant|Advisor|Executive|Officer|President|Vice President|VP|Chief|CTO|CEO|CFO|COO)?$/;
      const datePattern = /([A-Z][a-z]+\s+\d{4}|\d{1,2}\/\d{4}|\d{4})\s*[-–—]\s*([A-Z][a-z]+\s+\d{4}|\d{1,2}\/\d{4}|\d{4}|Present|Current)/i;
      
      if (companyPattern.test(line1) && titlePattern.test(line2) && datePattern.test(line3)) {
        const dateMatch = line3.match(datePattern);
        experiences.push({
          title: line2,
          company: line1,
          location: '',
          start_date: dateMatch ? dateMatch[1] : '',
          end_date: dateMatch ? dateMatch[2] : 'Present',
          description: ''
        });
        i += 2; // Skip the next 2 lines since we used them
      }
    }
  }
  
  // Final fallback: Very aggressive pattern matching - extract ANY text that looks like job info
  if (experiences.length === 0) {
    console.log('Trying final fallback parsing...');
    const allLines = text.split(/\r?\n/).map(line => line.trim()).filter(line => line.length > 2);
    
    // Look for lines that contain job-related keywords
    const jobKeywords = [
      'engineer', 'manager', 'director', 'analyst', 'developer', 'specialist', 
      'coordinator', 'assistant', 'executive', 'officer', 'president', 'vice president',
      'consultant', 'advisor', 'lead', 'senior', 'junior', 'associate', 'chief',
      'developer', 'designer', 'architect', 'scientist', 'researcher', 'administrator'
    ];
    
    // Look for company indicators
    const companyIndicators = ['inc', 'llc', 'corp', 'company', 'ltd', 'systems', 'solutions', 'technologies', 'group', 'associates'];
    
    // Look for date patterns anywhere in the text
    const datePattern = /(\d{4}|\w+\s+\d{4}|\d{1,2}\/\d{4})/g;
    
    let currentEntry = null;
    let descriptionLines = [];
    
    for (let i = 0; i < allLines.length; i++) {
      const line = allLines[i];
      const lowerLine = line.toLowerCase();
      
      // Check if line has job keywords
      const hasJobKeyword = jobKeywords.some(keyword => lowerLine.includes(keyword));
      // Check if line has company indicators
      const hasCompanyIndicator = companyIndicators.some(indicator => lowerLine.includes(indicator));
      // Check if line has dates
      const hasDate = datePattern.test(line);
      
      // If we find a line with job keyword or company indicator, start a new entry
      if ((hasJobKeyword || hasCompanyIndicator) && line.length > 5 && line.length < 100) {
        // Save previous entry if exists
        if (currentEntry && (currentEntry.title || currentEntry.company)) {
          if (descriptionLines.length > 0) {
            currentEntry.description = descriptionLines.join(' ').substring(0, 500);
          }
          experiences.push(currentEntry);
        }
        
        // Try to extract title and company from the line
        let title = '';
        let company = '';
        
        // Pattern: "Title at Company" or "Company - Title"
        const atPattern = /(.+?)\s+(?:at|@)\s+(.+)/i;
        const dashPattern = /(.+?)\s*[-–—]\s*(.+)/;
        
        if (atPattern.test(line)) {
          const match = line.match(atPattern);
          title = match[1].trim();
          company = match[2].trim();
        } else if (dashPattern.test(line)) {
          const match = line.match(dashPattern);
          // Try to determine which is title and which is company
          const foundKeyword = jobKeywords.find(k => match[1].toLowerCase().includes(k));
          if (hasJobKeyword && foundKeyword && match[1].toLowerCase().includes(foundKeyword)) {
            title = match[1].trim();
            company = match[2].trim();
          } else {
            company = match[1].trim();
            title = match[2].trim();
          }
        } else {
          // Single line - could be title or company
          if (hasJobKeyword) {
            title = line;
          } else if (hasCompanyIndicator) {
            company = line;
          } else {
            // Default to title if it's a reasonable length
            title = line;
          }
        }
        
        currentEntry = {
          title: title || '',
          company: company || '',
          location: '',
          start_date: '',
          end_date: 'Present',
          description: ''
        };
        descriptionLines = [];
      } 
      // If we have a current entry and find dates, add them
      else if (currentEntry && hasDate) {
        const dateMatch = line.match(/(\d{4}|\w+\s+\d{4}|\d{1,2}\/\d{4})\s*[-–—]\s*(\d{4}|\w+\s+\d{4}|\d{1,2}\/\d{4}|Present|Current)/i);
        if (dateMatch) {
          currentEntry.start_date = dateMatch[1] || '';
          currentEntry.end_date = dateMatch[2] || 'Present';
        }
      }
      // If we have a current entry and this looks like description
      else if (currentEntry && line.length > 10 && !hasDate && !hasJobKeyword && !hasCompanyIndicator) {
        descriptionLines.push(line);
      }
      // If we don't have a current entry but find a line with both job keyword and date, create entry
      else if (!currentEntry && hasJobKeyword && hasDate) {
        const dateMatch = line.match(/(\d{4}|\w+\s+\d{4}|\d{1,2}\/\d{4})\s*[-–—]\s*(\d{4}|\w+\s+\d{4}|\d{1,2}\/\d{4}|Present|Current)/i);
        const titleMatch = line.match(/([A-Z][A-Za-z\s&,.-]+(?:Manager|Engineer|Developer|Analyst|Specialist|Director|Lead|Senior|Junior|Associate|Coordinator|Consultant|Advisor|Executive|Officer|President|Vice President|VP|Chief|CTO|CEO|CFO|COO)?)/);
        
        if (titleMatch) {
          currentEntry = {
            title: titleMatch[1].trim(),
            company: '',
            location: '',
            start_date: dateMatch ? dateMatch[1] : '',
            end_date: dateMatch ? dateMatch[2] : 'Present',
            description: ''
          };
        }
      }
    }
    
    // Save last entry
    if (currentEntry && (currentEntry.title || currentEntry.company)) {
      if (descriptionLines.length > 0) {
        currentEntry.description = descriptionLines.join(' ').substring(0, 500);
      }
      experiences.push(currentEntry);
    }
  }
  
  // Clean and validate experiences
  // Be more lenient - accept entries with title OR company (not both required)
  const cleanedExperiences = experiences
    .filter(exp => (exp.title && exp.title.length > 2) || (exp.company && exp.company.length > 2))
    .map(exp => ({
      title: (exp.title || 'Position').trim(),
      company: (exp.company || 'Company').trim(),
      location: exp.location?.trim() || '',
      start_date: exp.start_date?.trim() || '',
      end_date: exp.end_date?.trim() || 'Present',
      description: exp.description?.trim().substring(0, 1000) || ''
    }));
  
  // Log for debugging
  if (cleanedExperiences.length > 0) {
    console.log(`Parsed ${cleanedExperiences.length} experience entries from PDF`);
  } else {
    console.warn('No experience entries found. Text sample:', text.substring(0, 500));
  }
  
  return cleanedExperiences;
};

/**
 * Main function to extract experience from PDF
 */
export const extractWorkExperienceFromPDF = async (file) => {
  try {
    // Extract text from PDF
    const text = await extractTextFromPDF(file);
    
    // Log extracted text for debugging (first 1000 chars)
    console.log('Extracted PDF text (first 1000 chars):', text.substring(0, 1000));
    
    // Parse experience from text
    const experiences = parseWorkExperienceFromText(text);
    
    console.log('Parsed experiences:', experiences);
    
    if (experiences.length === 0) {
      // Provide more helpful error message
      const textSample = text.substring(0, 500);
      console.warn('No experience found. Text sample:', textSample);
      throw new Error('No experience found in the PDF. The PDF may not contain experience information in a recognizable format. Please ensure the PDF contains job titles, company names, and dates.');
    }
    
    return experiences;
  } catch (error) {
    console.error('Error extracting experience from PDF:', error);
    // Re-throw with more context if it's our custom error
    if (error.message && error.message.includes('No experience found')) {
      throw error;
    }
    throw new Error(`Failed to extract experience from PDF: ${error.message || 'Unknown error'}`);
  }
};

export default {
  extractTextFromPDF,
  parseWorkExperienceFromText,
  extractWorkExperienceFromPDF
};
